import { useState, useEffect } from 'react';
import { adminApi } from '../services/api';
import toast from 'react-hot-toast';
import { Dialog, Tab } from '@headlessui/react';
import {
  CreditCardIcon,
  CurrencyDollarIcon,
  SparklesIcon,
  CubeIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  XMarkIcon,
  CheckIcon,
  UserGroupIcon,
  CircleStackIcon,
  ChartBarIcon,
  ArrowPathIcon,
  BoltIcon,
  RocketLaunchIcon,
  UserPlusIcon,
  ReceiptPercentIcon,
  ShareIcon
} from '@heroicons/react/24/outline';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

// Format currency amount
function formatCurrency(amount, currencyCode = 'IDR') {
  if (currencyCode === 'IDR') {
    return `Rp ${amount.toLocaleString('id-ID')}`;
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode
  }).format(amount);
}

// ==================== EDIT PLAN MODAL ====================
function EditPlanModal({ isOpen, onClose, plan, currencies, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    pricing: [],
    allowAdditionalSeats: true,
    lemonSqueezy: {
      productId: '',
      discountCode: ''
    }
  });

  // Initialize pricing for all currencies when plan or currencies change
  useEffect(() => {
    if (plan && currencies.length > 0) {
      // Build pricing array with all currencies, merging existing prices
      const existingPricingMap = {};
      (plan.pricing || []).forEach(p => {
        existingPricingMap[p.currencyCode] = {
          amount: p.amount,
          lemonSqueezyVariantId: p.lemonSqueezyVariantId || ''
        };
      });

      const fullPricing = currencies.map(currency => ({
        currencyCode: currency.code,
        amount: existingPricingMap[currency.code]?.amount ?? '',
        lemonSqueezyVariantId: existingPricingMap[currency.code]?.lemonSqueezyVariantId || '',
        symbol: currency.symbol,
        isDefault: currency.isDefault
      }));

      // Sort to put default currency first
      fullPricing.sort((a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0));

      setFormData({
        pricing: fullPricing,
        allowAdditionalSeats: plan.allowAdditionalSeats !== false,
        lemonSqueezy: {
          productId: plan.lemonSqueezy?.productId || '',
          discountCode: plan.lemonSqueezy?.discountCode || ''
        }
      });
    }
  }, [plan, currencies]);

  const updatePricing = (index, field, value) => {
    const newPricing = [...formData.pricing];
    newPricing[index] = { ...newPricing[index], [field]: value };
    setFormData({ ...formData, pricing: newPricing });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Only include pricing entries that have an amount set
      const validPricing = formData.pricing
        .filter(p => p.amount !== '' && p.amount !== null && p.amount !== undefined)
        .map(p => {
          const pricing = {
            currencyCode: p.currencyCode,
            amount: Number(p.amount)
          };
          // Include Lemon Squeezy Variant ID if set
          if (p.lemonSqueezyVariantId) pricing.lemonSqueezyVariantId = p.lemonSqueezyVariantId;
          return pricing;
        });

      // Build lemonSqueezy object with productId and discountCode
      const lemonSqueezy = {};
      if (formData.lemonSqueezy.productId) lemonSqueezy.productId = formData.lemonSqueezy.productId;
      if (formData.lemonSqueezy.discountCode) lemonSqueezy.discountCode = formData.lemonSqueezy.discountCode;

      await adminApi.updateSubscriptionPlan(plan._id, {
        pricing: validPricing,
        allowAdditionalSeats: formData.allowAdditionalSeats,
        lemonSqueezy: Object.keys(lemonSqueezy).length > 0 ? lemonSqueezy : undefined
      });
      toast.success('Plan updated successfully');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update plan');
    } finally {
      setLoading(false);
    }
  };

  if (!plan) return null;

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="card w-full max-w-lg max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <Dialog.Title className="text-lg font-semibold text-surface-100">
              Edit {plan.name} Plan Pricing
            </Dialog.Title>
            <button onClick={onClose} className="text-surface-400 hover:text-surface-100">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Lemon Squeezy Configuration */}
            <div className="bg-surface-700/50 rounded-lg p-4 space-y-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <CreditCardIcon className="w-5 h-5 text-blue-400" />
                  <span className="text-sm font-medium text-surface-300">Lemon Squeezy Product ID</span>
                </div>
                <input
                  type="text"
                  value={formData.lemonSqueezy.productId}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    lemonSqueezy: { ...formData.lemonSqueezy, productId: e.target.value } 
                  })}
                  className="input font-mono text-sm"
                  placeholder="Product ID..."
                />
                <p className="text-xs text-surface-500 mt-1">Same product for all currencies - prices are set per currency below</p>
              </div>
              
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <ReceiptPercentIcon className="w-5 h-5 text-green-400" />
                  <span className="text-sm font-medium text-surface-300">Beta Discount Code</span>
                  <span className="text-xs bg-green-600/20 text-green-300 px-2 py-0.5 rounded">50% Off for Founders</span>
                </div>
                <input
                  type="text"
                  value={formData.lemonSqueezy.discountCode}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    lemonSqueezy: { ...formData.lemonSqueezy, discountCode: e.target.value } 
                  })}
                  className="input font-mono text-sm"
                  placeholder="e.g., FOUNDERS50"
                />
                <p className="text-xs text-surface-500 mt-1">
                  Discount code from Lemon Squeezy. Applied automatically for beta users at checkout.
                </p>
              </div>
            </div>
            
            {/* Allow Additional Seats Toggle */}
            <div className="flex items-center justify-between p-4 bg-surface-700/50 rounded-lg">
              <div>
                <div className="text-sm font-medium text-surface-300">Allow Additional Seats</div>
                <div className="text-xs text-surface-500">
                  When enabled, users on this plan can purchase extra seats
                </div>
              </div>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, allowAdditionalSeats: !formData.allowAdditionalSeats })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  formData.allowAdditionalSeats ? 'bg-primary-500' : 'bg-surface-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    formData.allowAdditionalSeats ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <p className="text-xs text-surface-400">
              Set prices and Lemon Squeezy Variant IDs for each currency. Leave amount empty to hide this plan for that currency.
            </p>
            
            {/* Currency-specific pricing with Lemon Squeezy Variant IDs */}
            {formData.pricing.map((price, index) => (
              <div key={price.currencyCode} className="bg-surface-700/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-surface-300">
                      {price.currencyCode}
                    </span>
                    {price.isDefault && (
                      <span className="badge-success text-xs">Default</span>
                    )}
                  </div>
                  <span className="text-xs text-surface-500">{price.symbol}</span>
                </div>
                
                {/* Price Amount */}
                <div className="mb-3">
                  <label className="block text-xs text-surface-400 mb-1">
                    Monthly Price
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-400">
                      {price.symbol}
                    </span>
                    <input
                      type="number"
                      value={price.amount}
                      onChange={(e) => updatePricing(index, 'amount', e.target.value)}
                      className="input pl-10"
                      min="0"
                      placeholder="Not set"
                    />
                  </div>
                </div>
                
                {/* Lemon Squeezy Variant ID for this currency */}
                <div className="border-t border-surface-600 pt-3 mt-3">
                  <p className="text-xs font-medium text-blue-400 mb-2">Lemon Squeezy Variant ID ({price.currencyCode})</p>
                  <div>
                    <label className="block text-xs text-surface-500 mb-1">Monthly Price Variant ID</label>
                    <input
                      type="text"
                      value={price.lemonSqueezyVariantId || ''}
                      onChange={(e) => updatePricing(index, 'lemonSqueezyVariantId', e.target.value)}
                      className="input font-mono text-xs"
                      placeholder="Variant ID from Lemon Squeezy..."
                    />
                    <p className="text-xs text-surface-500 mt-1">Single variant - discounts are applied via discount code above</p>
                  </div>
                </div>
              </div>
            ))}

            <div className="flex gap-3 pt-4">
              <button type="button" onClick={onClose} className="btn-secondary flex-1">
                Cancel
              </button>
              <button type="submit" disabled={loading} className="btn-primary flex-1">
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}

// ==================== CURRENCY MODAL ====================
function CurrencyModal({ isOpen, onClose, currency, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    symbol: '',
    decimalPlaces: 2
  });

  useEffect(() => {
    if (currency) {
      setFormData({
        code: currency.code,
        name: currency.name,
        symbol: currency.symbol,
        decimalPlaces: currency.decimalPlaces
      });
    } else {
      setFormData({
        code: '',
        name: '',
        symbol: '',
        decimalPlaces: 2
      });
    }
  }, [currency]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (currency) {
        await adminApi.updateCurrency(currency._id, formData);
        toast.success('Currency updated successfully');
      } else {
        await adminApi.createCurrency(formData);
        toast.success('Currency created successfully');
      }
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save currency');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="card w-full max-w-md">
          <div className="flex items-center justify-between mb-6">
            <Dialog.Title className="text-lg font-semibold text-surface-100">
              {currency ? 'Edit Currency' : 'Add Currency'}
            </Dialog.Title>
            <button onClick={onClose} className="text-surface-400 hover:text-surface-100">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-surface-300 mb-1">
                  Currency Code
                </label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  className="input"
                  placeholder="USD"
                  maxLength={3}
                  disabled={!!currency}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-surface-300 mb-1">
                  Symbol
                </label>
                <input
                  type="text"
                  value={formData.symbol}
                  onChange={(e) => setFormData({ ...formData, symbol: e.target.value })}
                  className="input"
                  placeholder="$"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1">
                Currency Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input"
                placeholder="US Dollar"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1">
                Decimal Places
              </label>
              <input
                type="number"
                value={formData.decimalPlaces}
                onChange={(e) => setFormData({ ...formData, decimalPlaces: parseInt(e.target.value) })}
                className="input"
                min="0"
                max="4"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <button type="button" onClick={onClose} className="btn-secondary flex-1">
                Cancel
              </button>
              <button type="submit" disabled={loading} className="btn-primary flex-1">
                {loading ? 'Saving...' : currency ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}

// ==================== EDIT CREDIT PACK MODAL ====================
function EditCreditPackModal({ isOpen, onClose, pack, currencies, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    credits: 0,
    pricing: [],
    isFeatured: false,
    bonusCredits: 0,
    lemonSqueezy: {
      productId: ''
    }
  });

  // Initialize pricing for all currencies when pack or currencies change
  useEffect(() => {
    if (pack && currencies.length > 0) {
      // Build pricing array with all currencies, merging existing prices
      const existingPricingMap = {};
      (pack.pricing || []).forEach(p => {
        existingPricingMap[p.currencyCode] = {
          amount: p.amount,
          lemonSqueezyVariantId: p.lemonSqueezyVariantId || ''
        };
      });

      const fullPricing = currencies.map(currency => ({
        currencyCode: currency.code,
        amount: existingPricingMap[currency.code]?.amount ?? '',
        lemonSqueezyVariantId: existingPricingMap[currency.code]?.lemonSqueezyVariantId || '',
        symbol: currency.symbol,
        isDefault: currency.isDefault
      }));

      // Sort to put default currency first
      fullPricing.sort((a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0));

      setFormData({
        name: pack.name,
        description: pack.description || '',
        credits: pack.credits,
        pricing: fullPricing,
        isFeatured: pack.isFeatured || false,
        bonusCredits: pack.bonusCredits || 0,
        lemonSqueezy: {
          productId: pack.lemonSqueezy?.productId || ''
        }
      });
    }
  }, [pack, currencies]);

  const updatePricing = (index, field, value) => {
    const newPricing = [...formData.pricing];
    newPricing[index] = { ...newPricing[index], [field]: value };
    setFormData({ ...formData, pricing: newPricing });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Only include pricing entries that have a value set
      const validPricing = formData.pricing
        .filter(p => p.amount !== '' && p.amount !== null && p.amount !== undefined)
        .map(p => {
          const pricing = {
            currencyCode: p.currencyCode,
            amount: Number(p.amount)
          };
          if (p.lemonSqueezyVariantId) pricing.lemonSqueezyVariantId = p.lemonSqueezyVariantId;
          return pricing;
        });

      // Build lemonSqueezy object with productId
      const lemonSqueezy = {};
      if (formData.lemonSqueezy.productId) lemonSqueezy.productId = formData.lemonSqueezy.productId;

      await adminApi.updateCreditPack(pack._id, {
        name: formData.name,
        description: formData.description,
        credits: Number(formData.credits),
        bonusCredits: Number(formData.bonusCredits),
        pricing: validPricing,
        isFeatured: formData.isFeatured,
        lemonSqueezy: Object.keys(lemonSqueezy).length > 0 ? lemonSqueezy : undefined
      });
      toast.success('Credit pack updated successfully');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update credit pack');
    } finally {
      setLoading(false);
    }
  };

  if (!pack) return null;

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="card w-full max-w-lg max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <Dialog.Title className="text-lg font-semibold text-surface-100">
              Edit {pack.name}
            </Dialog.Title>
            <button onClick={onClose} className="text-surface-400 hover:text-surface-100">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1">
                Pack Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1">
                Description
              </label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-surface-300 mb-1">
                  Credits
                </label>
                <input
                  type="number"
                  value={formData.credits}
                  onChange={(e) => setFormData({ ...formData, credits: e.target.value })}
                  className="input"
                  min="1"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-surface-300 mb-1">
                  Bonus Credits
                </label>
                <input
                  type="number"
                  value={formData.bonusCredits}
                  onChange={(e) => setFormData({ ...formData, bonusCredits: e.target.value })}
                  className="input"
                  min="0"
                />
              </div>
            </div>

            {/* Lemon Squeezy Configuration */}
            <div className="bg-surface-700/50 rounded-lg p-4 space-y-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <CreditCardIcon className="w-5 h-5 text-blue-400" />
                  <span className="text-sm font-medium text-surface-300">Lemon Squeezy Product ID</span>
                </div>
                <input
                  type="text"
                  value={formData.lemonSqueezy.productId}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    lemonSqueezy: { ...formData.lemonSqueezy, productId: e.target.value } 
                  })}
                  className="input font-mono text-sm"
                  placeholder="Product ID..."
                />
                <p className="text-xs text-surface-500 mt-1">Same product for all currencies - prices are set per currency below</p>
              </div>
            </div>

            <div className="space-y-3">
              <label className="block text-sm font-medium text-surface-300">
                Pricing by Currency
              </label>
              <p className="text-xs text-surface-400">
                Set prices and Lemon Squeezy Variant IDs for each currency. Leave amount empty to hide this pack for that currency.
              </p>
              {formData.pricing.map((price, index) => (
                <div key={price.currencyCode} className="bg-surface-700/50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-surface-300">
                        {price.currencyCode}
                      </span>
                      {price.isDefault && (
                        <span className="badge-success text-xs">Default</span>
                      )}
                    </div>
                    <span className="text-xs text-surface-500">{price.symbol}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-surface-500 mb-1">Price</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-400">
                          {price.symbol}
                        </span>
                        <input
                          type="number"
                          value={price.amount}
                          onChange={(e) => updatePricing(index, 'amount', e.target.value)}
                          className="input pl-10"
                          min="0"
                          placeholder="Not set"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-surface-500 mb-1">Lemon Squeezy Variant ID</label>
                      <input
                        type="text"
                        value={price.lemonSqueezyVariantId || ''}
                        onChange={(e) => updatePricing(index, 'lemonSqueezyVariantId', e.target.value)}
                        className="input font-mono text-xs"
                        placeholder="Variant ID..."
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isFeatured"
                checked={formData.isFeatured}
                onChange={(e) => setFormData({ ...formData, isFeatured: e.target.checked })}
                className="w-4 h-4 rounded border-surface-600 bg-surface-700 text-primary-500"
              />
              <label htmlFor="isFeatured" className="text-sm text-surface-300">
                Featured (Best Value)
              </label>
            </div>

            <div className="flex gap-3 pt-4">
              <button type="button" onClick={onClose} className="btn-secondary flex-1">
                Cancel
              </button>
              <button type="submit" disabled={loading} className="btn-primary flex-1">
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}

// ==================== PLANS TAB ====================
function PlansTab() {
  const [plans, setPlans] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPlan, setEditingPlan] = useState(null);
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [plansRes, currenciesRes] = await Promise.all([
        adminApi.getSubscriptionPlans(),
        adminApi.getCurrencies()
      ]);
      setPlans(plansRes.data.plans || []);
      setCurrencies(currenciesRes.data.currencies || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  // Find the default currency
  const defaultCurrency = currencies.find(c => c.isDefault) || currencies[0];

  // Get pricing for a plan in the default currency
  const getPlanPrice = (plan) => {
    if (!defaultCurrency) return null;
    const pricing = plan.pricing?.find(p => p.currencyCode === defaultCurrency.code);
    return pricing || null;
  };

  const handleSeedData = async () => {
    if (!window.confirm('This will seed the default subscription plans, currencies, credit packs, and AI feature costs. Continue?')) {
      return;
    }
    
    setSeeding(true);
    try {
      await adminApi.seedSubscriptionData();
      toast.success('Subscription data seeded successfully!');
      loadData();
    } catch (error) {
      console.error('Seed error:', error);
      toast.error(error.response?.data?.error || 'Failed to seed data');
    } finally {
      setSeeding(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (plans.length === 0) {
    return (
      <div className="card text-center py-12">
        <CreditCardIcon className="w-16 h-16 text-surface-500 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-surface-100 mb-2">No Subscription Plans Found</h3>
        <p className="text-surface-400 mb-6 max-w-md mx-auto">
          The subscription plans haven't been set up yet. Click the button below to seed the default plans, currencies, credit packs, and AI feature costs.
        </p>
        <button
          onClick={handleSeedData}
          disabled={seeding}
          className="btn-primary"
        >
          {seeding ? (
            <>
              <ArrowPathIcon className="w-5 h-5 animate-spin" />
              Seeding Data...
            </>
          ) : (
            <>
              <PlusIcon className="w-5 h-5" />
              Seed Default Data
            </>
          )}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Currency indicator */}
      {defaultCurrency && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-surface-400">
            <CurrencyDollarIcon className="w-4 h-4" />
            <span>Showing prices in {defaultCurrency.code} ({defaultCurrency.name})</span>
          </div>
          <div className="text-xs text-surface-500">
            {currencies.length} currency{currencies.length !== 1 ? 'ies' : 'y'} configured
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {plans.map((plan) => {
          const pricing = getPlanPrice(plan);
          const hasDefaultCurrencyPrice = !!pricing;
          const configuredCurrencies = plan.pricing?.length || 0;

          return (
            <div key={plan._id} className="card hover:border-surface-600 transition-all">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={classNames(
                    "w-10 h-10 rounded-lg flex items-center justify-center",
                    plan.isContactSales ? "bg-purple-500/20" : "bg-primary-500/20"
                  )}>
                    <CreditCardIcon className={classNames(
                      "w-5 h-5",
                      plan.isContactSales ? "text-purple-400" : "text-primary-400"
                    )} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-surface-100">{plan.name}</h3>
                    <p className="text-xs text-surface-400">{plan.slug}</p>
                  </div>
                </div>
                {!plan.isContactSales && (
                  <button
                    onClick={() => setEditingPlan(plan)}
                    className="text-surface-400 hover:text-surface-100"
                  >
                    <PencilIcon className="w-5 h-5" />
                  </button>
                )}
              </div>

              {plan.isContactSales ? (
                <div className="bg-purple-500/10 text-purple-400 text-center py-3 rounded-lg mb-4">
                  Contact Sales
                </div>
              ) : hasDefaultCurrencyPrice ? (
                <div className="text-2xl font-bold text-surface-100 mb-4">
                  {pricing.amount === 0 ? 'Free' : formatCurrency(pricing.amount, defaultCurrency.code)}
                  {pricing.amount > 0 && (
                    <span className="text-sm font-normal text-surface-400">/month</span>
                  )}
                </div>
              ) : (
                <div className="text-2xl font-bold text-amber-400 mb-4">
                  Not set
                  <div className="text-xs font-normal text-amber-400/70">
                    No {defaultCurrency?.code} pricing
                  </div>
                </div>
              )}

              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-surface-300">
                  <UserGroupIcon className="w-4 h-4 text-surface-500" />
                  <span>{plan.features.userSeats} User Seat{plan.features.userSeats > 1 ? 's' : ''}</span>
                </div>
                <div className="flex items-center gap-2 text-surface-300">
                  <CircleStackIcon className="w-4 h-4 text-surface-500" />
                  <span>{plan.features.storageGB >= 1000 ? `${plan.features.storageGB / 1000} TB` : `${plan.features.storageGB} GB`} Storage</span>
                </div>
                <div className="flex items-center gap-2 text-surface-300">
                  <ChartBarIcon className="w-4 h-4 text-surface-500" />
                  <span>{plan.features.dashboardLimit === -1 ? 'Unlimited' : plan.features.dashboardLimit} Dashboard{plan.features.dashboardLimit !== 1 ? 's' : ''}</span>
                </div>
                <div className="flex items-center gap-2 text-surface-300">
                  <ArrowPathIcon className="w-4 h-4 text-surface-500" />
                  <span>{plan.features.manualSyncLimitPerDay} Syncs/day</span>
                </div>
                <div className="flex items-center gap-2 text-surface-300">
                  <BoltIcon className="w-4 h-4 text-surface-500" />
                  <span>
                    {plan.features.monthlyCredits > 0 
                      ? `${plan.features.monthlyCredits} Credits/mo`
                      : plan.features.oneTimeCredits > 0
                        ? `${plan.features.oneTimeCredits} Free Credits`
                        : 'Custom Credits'
                    }
                  </span>
                </div>
                <div className="flex items-center gap-2 text-surface-300">
                  <ShareIcon className="w-4 h-4 text-surface-500" />
                  <span>
                    {plan.features.sharedDashboardLimit === -1
                      ? 'Unlimited Shared Dashboards'
                      : plan.features.sharedDashboardLimit > 0
                        ? `${plan.features.sharedDashboardLimit} Shared Dashboard${plan.features.sharedDashboardLimit !== 1 ? 's' : ''}`
                        : 'No Dashboard Sharing'
                    }
                  </span>
                </div>
              </div>

              {/* Additional seats info */}
              {!plan.isContactSales && (
                <div className="mt-4 pt-4 border-t border-surface-700 text-xs">
                  {plan.allowAdditionalSeats === false ? (
                    <span className="text-amber-400">Additional seats not available</span>
                  ) : (
                    <span className="text-surface-400">
                      Additional seats: <span className="text-primary-400">Available</span>
                    </span>
                  )}
                </div>
              )}

              {/* Currency coverage indicator */}
              {!plan.isContactSales && (
                <div className="mt-4 pt-4 border-t border-surface-700">
                  <div className="flex items-center justify-center gap-1 text-xs text-surface-500">
                    <span>{configuredCurrencies} of {currencies.length} currencies configured</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <EditPlanModal
        isOpen={!!editingPlan}
        onClose={() => setEditingPlan(null)}
        plan={editingPlan}
        currencies={currencies}
        onSuccess={loadData}
      />
    </div>
  );
}

// ==================== CURRENCIES TAB ====================
function CurrenciesTab() {
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCurrency, setEditingCurrency] = useState(null);

  useEffect(() => {
    loadCurrencies();
  }, []);

  const loadCurrencies = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getCurrencies();
      setCurrencies(response.data.currencies);
    } catch (error) {
      toast.error('Failed to load currencies');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (currency) => {
    if (currency.isDefault) {
      toast.error('Cannot delete the default currency');
      return;
    }

    if (!window.confirm(`Delete currency ${currency.code}?`)) return;

    try {
      await adminApi.deleteCurrency(currency._id);
      toast.success('Currency deleted');
      loadCurrencies();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete currency');
    }
  };

  const handleSetDefault = async (currency) => {
    try {
      await adminApi.setDefaultCurrency(currency._id);
      toast.success(`${currency.code} is now the default currency`);
      loadCurrencies();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to set default');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <button onClick={() => { setEditingCurrency(null); setShowModal(true); }} className="btn-primary">
          <PlusIcon className="w-5 h-5" />
          Add Currency
        </button>
      </div>

      <div className="card overflow-hidden">
        <table className="w-full">
          <thead className="bg-surface-700/50">
            <tr>
              <th className="text-left text-sm font-medium text-surface-300 px-4 py-3">Code</th>
              <th className="text-left text-sm font-medium text-surface-300 px-4 py-3">Name</th>
              <th className="text-left text-sm font-medium text-surface-300 px-4 py-3">Symbol</th>
              <th className="text-left text-sm font-medium text-surface-300 px-4 py-3">Decimals</th>
              <th className="text-left text-sm font-medium text-surface-300 px-4 py-3">Status</th>
              <th className="text-right text-sm font-medium text-surface-300 px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-surface-700">
            {currencies.map((currency) => (
              <tr key={currency._id} className="hover:bg-surface-700/30">
                <td className="px-4 py-3">
                  <span className="font-mono font-medium text-surface-100">{currency.code}</span>
                </td>
                <td className="px-4 py-3 text-surface-300">{currency.name}</td>
                <td className="px-4 py-3 text-surface-300">{currency.symbol}</td>
                <td className="px-4 py-3 text-surface-300">{currency.decimalPlaces}</td>
                <td className="px-4 py-3">
                  {currency.isDefault ? (
                    <span className="badge-success">Default</span>
                  ) : currency.isActive ? (
                    <span className="badge-info">Active</span>
                  ) : (
                    <span className="badge-warning">Inactive</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    {!currency.isDefault && (
                      <button
                        onClick={() => handleSetDefault(currency)}
                        className="text-xs text-primary-400 hover:text-primary-300"
                      >
                        Set Default
                      </button>
                    )}
                    <button
                      onClick={() => { setEditingCurrency(currency); setShowModal(true); }}
                      className="text-surface-400 hover:text-surface-100"
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>
                    {!currency.isDefault && (
                      <button
                        onClick={() => handleDelete(currency)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CurrencyModal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setEditingCurrency(null); }}
        currency={editingCurrency}
        onSuccess={loadCurrencies}
      />
    </div>
  );
}

// ==================== CREDIT PACKS TAB ====================
function CreditPacksTab() {
  const [packs, setPacks] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPack, setEditingPack] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [packsRes, currenciesRes] = await Promise.all([
        adminApi.getCreditPacks(),
        adminApi.getCurrencies()
      ]);
      setPacks(packsRes.data.packs);
      setCurrencies(currenciesRes.data.currencies);
    } catch (error) {
      toast.error('Failed to load credit packs');
    } finally {
      setLoading(false);
    }
  };

  // Find the default currency
  const defaultCurrency = currencies.find(c => c.isDefault) || currencies[0];

  // Get pricing for a pack in the default currency
  const getPackPrice = (pack) => {
    if (!defaultCurrency) return null;
    const pricing = pack.pricing?.find(p => p.currencyCode === defaultCurrency.code);
    return pricing || null;
  };

  // Format price with the correct currency
  const formatPackPrice = (pack) => {
    const pricing = getPackPrice(pack);
    if (!pricing) return { price: null, perCredit: null };
    
    const totalCredits = pack.credits + (pack.bonusCredits || 0);
    return {
      price: formatCurrency(pricing.amount, defaultCurrency.code),
      perCredit: formatCurrency(pricing.amount / totalCredits, defaultCurrency.code)
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Currency indicator */}
      {defaultCurrency && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-surface-400">
            <CurrencyDollarIcon className="w-4 h-4" />
            <span>Showing prices in {defaultCurrency.code} ({defaultCurrency.name})</span>
          </div>
          <div className="text-xs text-surface-500">
            {currencies.length} currency{currencies.length !== 1 ? 'ies' : 'y'} configured
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {packs.map((pack) => {
          const { price, perCredit } = formatPackPrice(pack);
          const hasDefaultCurrencyPrice = !!getPackPrice(pack);
          const configuredCurrencies = pack.pricing?.length || 0;

          return (
            <div key={pack._id} className={classNames(
              "card hover:border-surface-600 transition-all relative",
              pack.isFeatured && "ring-2 ring-primary-500"
            )}>
              {pack.isFeatured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary-500 text-white text-xs px-3 py-1 rounded-full">
                  Best Value
                </div>
              )}

              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                    <CubeIcon className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-surface-100">{pack.name}</h3>
                    <p className="text-xs text-surface-400">{pack.slug}</p>
                  </div>
                </div>
                <button
                  onClick={() => setEditingPack(pack)}
                  className="text-surface-400 hover:text-surface-100"
                >
                  <PencilIcon className="w-5 h-5" />
                </button>
              </div>

              <div className="text-center py-4">
                <div className="text-3xl font-bold text-surface-100">
                  {pack.credits.toLocaleString()}
                </div>
                {pack.bonusCredits > 0 && (
                  <div className="text-sm text-emerald-400">
                    +{pack.bonusCredits} bonus
                  </div>
                )}
                <div className="text-sm text-surface-400">credits</div>
              </div>

              <div className="text-center text-2xl font-bold mb-2">
                {hasDefaultCurrencyPrice ? (
                  <span className="text-surface-100">{price}</span>
                ) : (
                  <span className="text-amber-400">Not set</span>
                )}
              </div>

              {hasDefaultCurrencyPrice && perCredit && (
                <div className="text-center text-xs text-surface-400">
                  {perCredit}/credit
                </div>
              )}

              {!hasDefaultCurrencyPrice && (
                <div className="text-center text-xs text-amber-400/70">
                  No {defaultCurrency?.code} pricing configured
                </div>
              )}

              <p className="text-sm text-surface-400 mt-4 text-center">
                {pack.description}
              </p>

              {/* Currency coverage indicator */}
              <div className="mt-4 pt-4 border-t border-surface-700">
                <div className="flex items-center justify-center gap-1 text-xs text-surface-500">
                  <span>{configuredCurrencies} of {currencies.length} currencies configured</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <EditCreditPackModal
        isOpen={!!editingPack}
        onClose={() => setEditingPack(null)}
        pack={editingPack}
        currencies={currencies}
        onSuccess={loadData}
      />
    </div>
  );
}

// ==================== AI COSTS TAB ====================
function AICostsTab() {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editValue, setEditValue] = useState('');

  useEffect(() => {
    loadFeatures();
  }, []);

  const loadFeatures = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getAIFeatureCosts();
      setFeatures(response.data.features);
    } catch (error) {
      toast.error('Failed to load AI feature costs');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (feature) => {
    try {
      await adminApi.updateAIFeatureCost(feature._id, {
        creditCost: parseInt(editValue)
      });
      toast.success('Cost updated');
      setEditing(null);
      loadFeatures();
    } catch (error) {
      toast.error('Failed to update cost');
    }
  };

  const categoryLabels = {
    data_source: 'Data Source',
    analysis: 'Analysis',
    dashboard: 'Dashboard',
    chat: 'Chat',
    data_studio: 'Data Studio'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="card overflow-hidden">
      <table className="w-full">
        <thead className="bg-surface-700/50">
          <tr>
            <th className="text-left text-sm font-medium text-surface-300 px-4 py-3">Feature</th>
            <th className="text-left text-sm font-medium text-surface-300 px-4 py-3">Category</th>
            <th className="text-left text-sm font-medium text-surface-300 px-4 py-3">Description</th>
            <th className="text-right text-sm font-medium text-surface-300 px-4 py-3">Credit Cost</th>
            <th className="text-right text-sm font-medium text-surface-300 px-4 py-3 w-24">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-surface-700">
          {features.map((feature) => (
            <tr key={feature._id} className="hover:bg-surface-700/30">
              <td className="px-4 py-3">
                <div className="font-medium text-surface-100">{feature.name}</div>
                <div className="text-xs text-surface-500 font-mono">{feature.featureKey}</div>
              </td>
              <td className="px-4 py-3">
                <span className="badge-info">
                  {categoryLabels[feature.category] || feature.category}
                </span>
              </td>
              <td className="px-4 py-3 text-surface-400 text-sm max-w-xs truncate">
                {feature.description}
              </td>
              <td className="px-4 py-3 text-right">
                {editing === feature._id ? (
                  <input
                    type="number"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    className="input w-20 text-right"
                    min="0"
                    autoFocus
                  />
                ) : (
                  <span className="font-mono font-medium text-surface-100">
                    {feature.creditCost}
                  </span>
                )}
              </td>
              <td className="px-4 py-3 text-right">
                {editing === feature._id ? (
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => handleSave(feature)}
                      className="text-emerald-400 hover:text-emerald-300"
                    >
                      <CheckIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setEditing(null)}
                      className="text-surface-400 hover:text-surface-100"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => { setEditing(feature._id); setEditValue(feature.creditCost.toString()); }}
                    className="text-surface-400 hover:text-surface-100"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ==================== ADDITIONAL SEATS TAB ====================
function AdditionalSeatsTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [config, setConfig] = useState({
    pricing: [],
    lemonSqueezy: {
      productId: ''
    }
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [seatsRes, currenciesRes] = await Promise.all([
        adminApi.getAdditionalSeatsConfig(),
        adminApi.getCurrencies()
      ]);
      
      const currencyList = currenciesRes.data.currencies || [];
      setCurrencies(currencyList);
      
      // Build pricing array with all currencies
      const existingPricing = seatsRes.data.additionalSeats?.pricing || [];
      const existingPricingMap = {};
      existingPricing.forEach(p => {
        existingPricingMap[p.currencyCode] = {
          pricePerSeat: p.pricePerSeat,
          lemonSqueezyVariantId: p.lemonSqueezyVariantId || ''
        };
      });

      const fullPricing = currencyList.map(currency => ({
        currencyCode: currency.code,
        pricePerSeat: existingPricingMap[currency.code]?.pricePerSeat ?? '',
        lemonSqueezyVariantId: existingPricingMap[currency.code]?.lemonSqueezyVariantId || '',
        symbol: currency.symbol,
        isDefault: currency.isDefault
      }));

      // Sort to put default currency first
      fullPricing.sort((a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0));

      setConfig({
        pricing: fullPricing,
        lemonSqueezy: { 
          productId: seatsRes.data.additionalSeats?.lemonSqueezy?.productId || ''
        }
      });
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load additional seats configuration');
    } finally {
      setLoading(false);
    }
  };

  const updatePricing = (index, field, value) => {
    const newPricing = [...config.pricing];
    newPricing[index] = { ...newPricing[index], [field]: value };
    setConfig({ ...config, pricing: newPricing });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Only include pricing entries that have a value set
      const validPricing = config.pricing
        .filter(p => p.pricePerSeat !== '' && p.pricePerSeat !== null && p.pricePerSeat !== undefined)
        .map(p => {
          const pricing = {
            currencyCode: p.currencyCode,
            pricePerSeat: Number(p.pricePerSeat)
          };
          if (p.lemonSqueezyVariantId) pricing.lemonSqueezyVariantId = p.lemonSqueezyVariantId;
          return pricing;
        });

      // Build lemonSqueezy object with productId
      const lemonSqueezy = {};
      if (config.lemonSqueezy.productId) lemonSqueezy.productId = config.lemonSqueezy.productId;

      await adminApi.updateAdditionalSeatsConfig({
        pricing: validPricing,
        lemonSqueezy: Object.keys(lemonSqueezy).length > 0 ? lemonSqueezy : undefined
      });
      
      toast.success('Additional seats configuration saved');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  // Find the default currency
  const defaultCurrency = currencies.find(c => c.isDefault) || currencies[0];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="card">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-primary-500/20 flex items-center justify-center">
            <UserPlusIcon className="w-6 h-6 text-primary-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-surface-100">Additional Seats Pricing</h2>
            <p className="text-sm text-surface-400">
              Configure the price for additional seats. This applies to all plans that allow extra seats.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Lemon Squeezy Configuration */}
          <div className="bg-surface-700/50 rounded-lg p-4 space-y-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <CreditCardIcon className="w-5 h-5 text-blue-400" />
                <span className="text-sm font-medium text-surface-300">Lemon Squeezy Product ID</span>
              </div>
              <input
                type="text"
                value={config.lemonSqueezy.productId}
                onChange={(e) => setConfig({
                  ...config,
                  lemonSqueezy: { ...config.lemonSqueezy, productId: e.target.value }
                })}
                className="input font-mono text-sm"
                placeholder="Product ID..."
              />
              <p className="text-xs text-surface-500 mt-1">Same product for all currencies - prices are set per currency below</p>
            </div>
          </div>

          {/* Pricing Section */}
          <div>
            <h3 className="text-sm font-medium text-surface-300 mb-3">
              Price per Seat (Monthly)
            </h3>
            <p className="text-xs text-surface-400 mb-4">
              Set the monthly price and Lemon Squeezy Variant ID for each additional seat in each currency.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {config.pricing.map((price, index) => (
                <div key={price.currencyCode} className="bg-surface-700/50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-surface-300">
                        {price.currencyCode}
                      </span>
                      {price.isDefault && (
                        <span className="badge-success text-xs">Default</span>
                      )}
                    </div>
                    <span className="text-xs text-surface-500">{price.symbol}</span>
                  </div>
                  <div className="space-y-2">
                    <div>
                      <label className="block text-xs text-surface-500 mb-1">Price</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-400">
                          {price.symbol}
                        </span>
                        <input
                          type="number"
                          value={price.pricePerSeat}
                          onChange={(e) => updatePricing(index, 'pricePerSeat', e.target.value)}
                          className="input pl-10"
                          min="0"
                          placeholder="Not set"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-surface-500 mb-1">Lemon Squeezy Variant ID</label>
                      <input
                        type="text"
                        value={price.lemonSqueezyVariantId || ''}
                        onChange={(e) => updatePricing(index, 'lemonSqueezyVariantId', e.target.value)}
                        className="input font-mono text-xs"
                        placeholder="Variant ID..."
                      />
                    </div>
                  </div>
                  <p className="text-xs text-surface-500 mt-2">per seat/month</p>
                </div>
              ))}
            </div>
          </div>

          <button type="submit" disabled={saving} className="btn-primary w-full">
            {saving ? 'Saving...' : 'Save Additional Seats Configuration'}
          </button>
        </form>
      </div>

      {/* Info Card */}
      <div className="card border-blue-500/30 bg-blue-500/5">
        <div className="flex items-start gap-3">
          <UserPlusIcon className="w-6 h-6 text-blue-400 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-surface-100">How Additional Seats Work</h3>
            <ul className="text-surface-400 text-sm mt-2 space-y-1 list-disc list-inside">
              <li>Plans specify a base number of seats included</li>
              <li>Users can purchase additional seats if the plan allows it</li>
              <li>All additional seats are priced the same regardless of plan</li>
              <li>Seat charges are recurring (monthly) and managed by Lemon Squeezy</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== LEMON SQUEEZY SETTINGS TAB ====================
function LemonSqueezySettingsTab() {
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    enterpriseProductId: ''
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await adminApi.getLemonSqueezySettings();
      setSettings({
        enterpriseProductId: response.data.lemonSqueezy?.enterpriseProductId || ''
      });
    } catch (error) {
      console.error('Failed to load Lemon Squeezy settings:', error);
      toast.error('Failed to load Lemon Squeezy settings');
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await adminApi.updateLemonSqueezySettings({
        enterpriseProductId: settings.enterpriseProductId
      });
      toast.success('Lemon Squeezy settings saved successfully');
    } catch (error) {
      console.error('Failed to save Lemon Squeezy settings:', error);
      toast.error(error.response?.data?.error || 'Failed to save Lemon Squeezy settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="card">
        <div className="p-6">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-surface-100 flex items-center gap-2">
                <CreditCardIcon className="w-5 h-5 text-primary-400" />
                Lemon Squeezy Configuration
              </h3>
              <p className="text-sm text-surface-400 mt-1">
                Configure Lemon Squeezy product IDs for payment processing
              </p>
            </div>
          </div>

          <div className="space-y-6">
            {/* Enterprise Product ID */}
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-2">
                Enterprise Invoice Product ID
              </label>
              <input
                type="text"
                value={settings.enterpriseProductId}
                onChange={(e) => setSettings({ ...settings, enterpriseProductId: e.target.value })}
                className="input"
                placeholder="pro_01abc123..."
              />
              <p className="text-xs text-surface-500 mt-2">
                This product is used for generating flexible enterprise invoices with variable amounts. 
                Create a product in Lemon Squeezy called "Enterprise Invoice" and paste its ID here.
              </p>
            </div>

            {/* Instructions */}
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-300 mb-2">How to get your Product ID:</h4>
              <ol className="text-xs text-blue-200 space-y-1 list-decimal list-inside">
                <li>Go to your Lemon Squeezy Dashboard → Catalog → Products</li>
                <li>Create a new product called "Enterprise Invoice"</li>
                <li>Copy the Product ID (starts with "pro_")</li>
                <li>Paste it in the field above and click Save</li>
              </ol>
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-4 border-t border-surface-700">
              <button
                onClick={handleSave}
                disabled={loading}
                className="btn-primary"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckIcon className="w-5 h-5 mr-2" />
                    Save Settings
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Info Card */}
      <div className="card">
        <div className="p-6">
          <h4 className="text-sm font-medium text-surface-200 mb-3">About Enterprise Invoicing</h4>
          <div className="text-sm text-surface-400 space-y-2">
            <p>
              The Enterprise Invoice product allows you to create flexible payment links with variable amounts each month.
            </p>
            <p>
              When an enterprise customer has postpaid billing with "Payment Gateway" selected, you can generate
              a custom payment link based on their usage (license fee + storage + credits).
            </p>
            <p className="text-xs text-surface-500 mt-4">
              Note: Individual subscription plans have their own Lemon Squeezy Product/Variant IDs configured in the Plans tab.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== MAIN COMPONENT ====================
export default function SubscriptionPlans() {
  const tabs = [
    { name: 'Plans', icon: CreditCardIcon, component: PlansTab },
    { name: 'Currencies', icon: CurrencyDollarIcon, component: CurrenciesTab },
    { name: 'Credit Packs', icon: CubeIcon, component: CreditPacksTab },
    { name: 'Additional Seats', icon: UserPlusIcon, component: AdditionalSeatsTab },
    { name: 'AI Feature Costs', icon: SparklesIcon, component: AICostsTab },
    { name: 'Lemon Squeezy Settings', icon: BoltIcon, component: LemonSqueezySettingsTab },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-surface-100">Subscription Plans</h1>
        <p className="text-surface-400 mt-1">Manage pricing, currencies, credit packs, and AI feature costs</p>
      </div>

      <Tab.Group>
        <Tab.List className="flex gap-1 bg-surface-800 p-1 rounded-lg">
          {tabs.map((tab) => (
            <Tab
              key={tab.name}
              className={({ selected }) =>
                classNames(
                  'flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-all',
                  selected
                    ? 'bg-primary-500 text-white'
                    : 'text-surface-400 hover:text-surface-100 hover:bg-surface-700'
                )
              }
            >
              <tab.icon className="w-4 h-4" />
              {tab.name}
            </Tab>
          ))}
        </Tab.List>

        <Tab.Panels className="mt-6">
          {tabs.map((tab) => (
            <Tab.Panel key={tab.name}>
              <tab.component />
            </Tab.Panel>
          ))}
        </Tab.Panels>
      </Tab.Group>
    </div>
  );
}
